import {
  Box,
  ButtonBase,
  Card,
  keyframes,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { formatCurrency } from '../../../utils/formatUtils';
import { CheckIcon } from '../../ui/icons';
import MoveIcon from '../../ui/icons/MoveIcon';
import WalletLogo from './WalletsList/WalletLogo';

const blinkAnimation = keyframes`
  0%, 100% { background-color: transparent; }
  50% { background-color: var(--highlight-color); }
`;

export interface BaseWalletItemProps {
  wallet: Wallet;
  onClick?: (wallet: Wallet) => void;
  selected?: boolean;
  showCheckbox?: boolean;
  showBalance?: boolean;
  dragHandleProps?: React.HTMLAttributes<HTMLElement>;
  isDragging?: boolean;
  disableGutters?: boolean;
  highlight?: boolean;
}

const BaseWalletItem = ({
  wallet,
  onClick,
  selected = false,
  showCheckbox = false,
  showBalance = true,
  dragHandleProps,
  isDragging = false,
  disableGutters = false,
  highlight = false,
}: BaseWalletItemProps) => {
  const theme = useTheme();

  const handleClick = (e: React.MouseEvent) => {
    // Don't trigger click if clicking on drag handle
    const target = e.target as HTMLElement;
    if (target.closest('[data-drag-handle]')) {
      return;
    }
    onClick?.(wallet);
  };

  return (
    <Card
      variant="outlined"
      sx={{
        width: '100%',
        marginBottom: disableGutters ? 0 : theme.spacing(1),
        borderColor: selected ? theme.palette.blue[300] : highlight ? theme.palette.teal[300] : undefined,
        cursor: isDragging ? 'grabbing' : 'default',
        '--highlight-color': theme.palette.teal[50],
        ...(highlight && {
          animation: `${blinkAnimation} 1s ease-in-out 3`,
        }),
      }}
    >
      <ButtonBase
        sx={{
          py: 1,
          px: 2,
          pr: dragHandleProps ? 5 : 2,
          width: '100%',
          textAlign: 'left',
          backgroundColor: selected ? theme.palette.teal[50] : 'transparent',
          cursor: isDragging ? 'grabbing' : 'pointer',
        }}
        onClick={handleClick}
      >
        <Stack
          useFlexGap
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <WalletLogo type={wallet.type.toLowerCase()} />
            <Typography variant="body1">{wallet.name}</Typography>
          </Stack>
          <Stack useFlexGap direction="row" alignItems="center" spacing={1}>
            {showBalance && (
              <Typography variant="h4">{formatCurrency(wallet.balance)}</Typography>
            )}
            {showCheckbox && (
              <Card
                elevation={0}
                sx={{
                  borderRadius: 0.5,
                  width: 24,
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: selected ? theme.palette.primary.main : 'transparent',
                  border: selected
                    ? 'none'
                    : `2px solid ${theme.palette.grey[300]}`,
                  color: theme.palette.primary.contrastText,
                }}
              >
                {selected && <CheckIcon />}
              </Card>
            )}
          </Stack>
        </Stack>
        {dragHandleProps && (
          <Box
            {...dragHandleProps}
            data-drag-handle
            sx={{
              p: 0,
              top: 0,
              right: 0,
              width: 40,
              height: '100%',
              position: 'absolute',
              fontSize: 24,
              color: isDragging ? theme.palette.text.primary : theme.palette.text.secondary,
              cursor: isDragging ? 'grabbing' : 'grab',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              touchAction: 'none',
              WebkitTapHighlightColor: 'transparent',
              userSelect: 'none',
            }}
          >
            <MoveIcon />
          </Box>
        )}
      </ButtonBase>
    </Card>
  );
};

export default BaseWalletItem;
